import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { useBuyEnergyForDiceGameMutation } from '@/api/queries/game';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { SendAnalitics } from '@/common/lib/playdeck';
import { CoinBalanceTopUp } from '@/components/CoinBalanceTopUp/CoinBalanceTopUp';
import { FullRecharge } from '@/components/common/FullRecharge/FullRecharge';
import { LightColorEffect } from '@/components/common/LightColorEffect/LightColorEffect';
import { H1, H2, H3, StringSmall } from '@/components/Typography/Typography';
import { PurpleButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import { updateCoinBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import flashPng from '/img/dice-game/flash-huge.png';
import flashWebp from '/img/dice-game/flash-huge.webp';
import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface RechargeEnergyProps {
  isRechargeEnergyOpen: boolean;
  closeRechargeEnergy: () => void;
}

const RechargeEnergy: FC<RechargeEnergyProps> = ({
  isRechargeEnergyOpen,
  closeRechargeEnergy,
}) => {
  const vip = useCheckVIPstatus();
  const { t } = useTranslation();
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const [buyDiceEnergy, { isLoading }] = useBuyEnergyForDiceGameMutation();
  const dispatch = useAppDispatch();

  const price = 20;
  const isEnabledForBuy = (tgemBalance || 0) >= price;

  const onClick = (): void => {
    if (isEnabledForBuy) {
      buyDiceEnergy()
        .unwrap()
        .then((response) => {
          dispatch(updateCoinBalance(response.tgem));
          SendAnalitics.coinSpend({
            amount: price,
            type: 'roll_dice_energy',
            item_name: 'dice_energy',
          });
          showToast(t('You have successfully restored your energy'), {
            img: flashPng,
            webp: flashWebp,
          });
          closeRechargeEnergy();
        })
        .catch((error) => {
          console.error(error);
          showToast(t('Something went wrong, please try again'));
        });
    }
  };

  return (
    <AnimatePresence>
      {isRechargeEnergyOpen && (
        <motion.div
          className={css.rechargeEnergy}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="w-full flex flex-col items-center relative z-[1]">
            <CoinBalanceTopUp
              className="!h-12 !pl-2 !pr-3 py-[0.625rem] mb-[5.125rem]"
              contenctClassName="!items-start"
              compare={Infinity}
              iconSize="1.75rem"
            />
            <H1 weight="extrabold" className="mb-6">
              {t('Purchase')}
            </H1>
            <H2 weight="bold" className="mb-2">
              {t('Power up your game!')}
            </H2>
            <StringSmall className="!text-white/80 text-center mb-5 max-w-[17.125rem]">
              {t('Low energy? Recharge now and continue exploring the field.')}
            </StringSmall>
          </div>
          <div className="relative w-24 h-24">
            <Picture
              className="w-full relative z-[1]"
              img={flashPng}
              webp={flashWebp}
              alt="dice"
            />
            <LightColorEffect
              lightClassName={css.yellowGradient}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85.04%] -z-[1]"
            />
          </div>
          <div className="flex flex-col items-center w-full relative z-[1] pt-4">
            <span className="font-black text-[2.5rem] leading-none mb-3">
              +{vip ? 15 : 10}
            </span>
            <FullRecharge className="mb-10" />
            <div className="w-[12.125rem] mb-[7.75rem]">
              <PurpleButton
                inactive={!isEnabledForBuy}
                isLoading={isLoading}
                disabled={isLoading || !isEnabledForBuy}
                onClick={onClick}
                className="flex items-center gap-1"
              >
                <H3
                  weight="extrabold"
                  className={cn(css.buttonText, '!text-white/80 mr-1')}
                >
                  {t('Buy for')}
                </H3>
                <Picture
                  className="w-6 h-6 shrink-0"
                  img={tgemPng}
                  webp={tgemWebp}
                  alt="tgem"
                />
                <H3
                  weight="extrabold"
                  className={cn(css.buttonText, '!text-white/80')}
                >
                  {price}
                </H3>
              </PurpleButton>
            </div>
            <Close
              onClick={closeRechargeEnergy}
              imageVariant="modal"
              className="!w-16 !h-16 shrink-0"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { RechargeEnergy };
